import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, CheckCircle2, Trash2, Film, AlertTriangle, Info } from 'lucide-react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { can, RUOLI_SOCIAL } from '../lib/ruoli';
import { fmtDurata, fmtSize } from '../lib/produzione';
import ReviewPlayer from './ReviewPlayer';

const isVid = (n) => /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(n || '');

/**
 * Versioni del montaggio (v1, v2…) + player con commenti al timecode.
 * Props: rid, mediaUrl (array media di pubblicazione), onChanged() dopo azioni che toccano la riga.
 */
export default function VersioniReview({ rid, mediaUrl = [], onChanged }) {
  const { success, error } = useToast();
  const { user } = useAuth();
  const [versioni, setVersioni] = useState([]);
  const [commenti, setCommenti] = useState([]);
  const [sel, setSel] = useState(null);          // id versione selezionata
  const [upload, setUpload] = useState(null);    // { nome, pct }
  const [problemi, setProblemi] = useState(null); // esito specifiche dell'ultimo upload
  const fileRef = useRef(null);

  const load = useCallback(async (selId) => {
    try {
      const [v, c] = await Promise.all([api.get(`/ped/${rid}/versioni`), api.get(`/ped/${rid}/commenti`)]);
      setVersioni(v.data || []);
      setCommenti(c.data || []);
      setSel(s => selId ?? (s && (v.data || []).some(x => x.id === s) ? s : (v.data?.[0]?.id ?? null)));
    } catch { /* toast globale */ }
  }, [rid]);

  useEffect(() => { load(); }, [load]);

  const carica = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024 * 1024) { error('File oltre 2GB'); return; }
    const note = window.prompt('Note per questa versione (opzionale):', '') ?? '';
    const fd = new FormData();
    fd.append('file', file); fd.append('note', note);
    setUpload({ nome: file.name, pct: 0 }); setProblemi(null);
    try {
      const r = await api.post(`/ped/${rid}/versioni`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setUpload({ nome: file.name, pct: Math.round((e.loaded / (e.total || file.size)) * 100) }),
      });
      success(`Versione v${r.data.numero} caricata`);
      setProblemi(r.data.problemi || []);
      await load(r.data.id);
      onChanged && onChanged();
    } catch { /* toast globale */ }
    setUpload(null);
  };

  const usa = async (v) => {
    if (!window.confirm(`Usare v${v.numero} come media di pubblicazione? Sostituisce i media attuali.`)) return;
    try { await api.post(`/ped/versioni/${v.id}/usa`); success(`v${v.numero} impostata per la pubblicazione`); onChanged && onChanged(); }
    catch { /* toast globale */ }
  };

  const elimina = async (v) => {
    if (!window.confirm(`Eliminare la versione v${v.numero} e i suoi commenti?`)) return;
    try { await api.delete(`/ped/versioni/${v.id}`); success('Versione eliminata'); await load(); }
    catch { /* toast globale */ }
  };

  const addCommento = async (testo, tc) => {
    const r = await api.post(`/ped/${rid}/commenti`, { testo, versione_id: sel, timecode_sec: tc });
    setCommenti(cs => [...cs, r.data]);
  };
  const toggle = async (c) => {
    const r = await api.patch(`/ped/commenti/${c.id}`, { risolto: !c.risolto });
    setCommenti(cs => cs.map(x => x.id === c.id ? r.data : x));
  };
  const del = async (c) => {
    if (!window.confirm('Eliminare il commento?')) return;
    await api.delete(`/ped/commenti/${c.id}`);
    setCommenti(cs => cs.filter(x => x.id !== c.id));
  };

  const v = versioni.find(x => x.id === sel);
  const inPubblicazione = (x) => mediaUrl.some(u => (u || '').endsWith(x.url));
  const commentiVersione = commenti
    .filter(c => sel ? (c.versione_id === sel || c.versione_id == null) : c.versione_id == null)
    .map(c => ({ ...c, mio: c.autore_id && String(c.autore_id) === String(user?.id) }));
  const media = v ? { url: v.url, video: isVid(v.url), thumb: v.thumb_url } : null;

  return (
    <div>
      {/* barra versioni */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {versioni.length === 0 && !upload && (
          <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>Nessuna versione. Carica il primo montaggio: il contenuto passa in review interna.</span>
        )}
        {versioni.map(x => {
          const on = x.id === sel;
          return (
            <button key={x.id} onClick={() => setSel(x.id)} title={x.note || x.nome}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${on ? 'var(--cyan)' : 'var(--border)'}`, background: on ? 'var(--cyan-dim)' : 'var(--surface)', color: on ? 'var(--cyan)' : 'var(--text-muted)' }}>
              v{x.numero}
              {inPubblicazione(x) && <CheckCircle2 size={12} color="var(--green)" />}
              {Number(x.commenti_aperti) > 0 && <span style={{ fontSize: 10, background: 'var(--yellow)', color: '#1a1200', borderRadius: 7, padding: '0 5px' }}>{x.commenti_aperti}</span>}
            </button>
          );
        })}
        <button onClick={() => fileRef.current?.click()} disabled={!!upload} className="eden-btn-primary" style={{ marginLeft: 'auto', fontSize: 12 }}>
          <Upload size={13} /> {upload ? `Carico ${upload.pct}%…` : 'Carica nuova versione'}
        </button>
        <input ref={fileRef} type="file" accept="video/*,image/*" hidden onChange={e => { carica(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      {upload && (
        <div style={{ height: 5, background: 'var(--bg3)', borderRadius: 999, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ width: `${upload.pct}%`, height: '100%', background: 'var(--cyan)', transition: 'width 0.2s' }} />
        </div>
      )}

      {problemi && problemi.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {problemi.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: p.livello === 'blocco' ? 'var(--red)' : 'var(--yellow)' }}>
              <AlertTriangle size={13} /> {p.testo}
            </div>
          ))}
        </div>
      )}

      {v && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            <Film size={13} />
            <span style={{ color: 'var(--text)', fontWeight: 600 }}>{v.nome}</span>
            {v.durata_sec != null && <span>{fmtDurata(v.durata_sec)}</span>}
            {v.larghezza && <span>{v.larghezza}×{v.altezza}</span>}
            <span>{fmtSize(v.peso)}</span>
            {v.caricato_da_nome && <span>da {v.caricato_da_nome}</span>}
            <span>{new Date(v.creato_il).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {!inPubblicazione(v) && (
                <button onClick={() => usa(v)} style={smallBtn('var(--green)')}><CheckCircle2 size={12} /> Usa per la pubblicazione</button>
              )}
              {(String(v.caricato_da) === String(user?.id) || can(user, RUOLI_SOCIAL)) && (
                <button onClick={() => elimina(v)} style={smallBtn('var(--text-dim)')} title="Elimina versione"><Trash2 size={12} /></button>
              )}
            </div>
          </div>
          {v.note && (
            <div style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg3)', borderRadius: 8, padding: '7px 10px', marginBottom: 10 }}>
              <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} /> {v.note}
            </div>
          )}
        </>
      )}

      {(v || commentiVersione.length > 0) && (
        <ReviewPlayer media={media} commenti={commentiVersione} onAdd={addCommento} onToggleRisolto={toggle}
          onDelete={del} canDeleteAll={can(user, RUOLI_SOCIAL)}
          placeholder={v ? `Commento su v${v.numero}… (Ctrl+Invio per inviare)` : 'Commento generale…'} />
      )}
    </div>
  );
}

const smallBtn = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, padding: '4px 9px', borderRadius: 7,
  border: '1px solid var(--border)', background: 'var(--surface)', color, cursor: 'pointer',
});
